<script>
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
import {
  orderBy, uniqBy, isEmpty, filter,
} from 'lodash';
import markersInDirectory from 'api/markersInDirectory';
import setMarkerForActivity from 'api/setMarkerForActivity';
import { C_ANSWER_TEMPLATE_INTERPOLATION_CODES } from 'src/store/modules/app';
import {
  toast,
  getDirectoriesFromMarkers,
  parseAnswerTemplateForReplacement,
} from 'src/utils';

function getMarkersFromTemplates(acc, elem) {
  const joinArrs = [...acc, ...elem.markers];
  return uniqBy(joinArrs, 'id');
}

export default {
  props: ['value'],
  data() {
    return {
      requestTypeId: null,
      errorNotification: false,
      isMarkerEdit: false,
      activeMarker: null,
      activeDirectory: null,
      markers: [],
      loadingFormData: true,
      loadingMarks: false,
      loadingTemplates: false,
      setMarkerError: false,
      templateSearchText: '',
      sentForSearchText: '',
      searchInputDisabled: false,
      isSearchMode: false,
      selectedTemplate: null,
      selectedTemplateActiveDirectory: null,
      selectedTemplateActiveMarker: null,
      selectedTemplateMarkers: [],
      serviceDirectory: {
        id: 'serviceDirectory',
        markers: [],
        name: this.$t('T_NO_THEME'),
      },
      isSearchLoading: false,
      showActiveDirectoryTooltip: false,
    };
  },
  async mounted() {
    await this.loadAnswerTemplates();
    this.init();
  },
  watch: {
    templateSearchText(val) {
      if (!val && this.isSearchMode) {
        this.CLEAR_ANSWER_TEMPLATES_LOADED();
        this.emptyData();
        this.isSearchMode = false;
        if (this.sortedDirectories.length) {
          this.onshowMarks(this.sortedDirectories[0]);
        }
      }
    },
  },
  computed: {
    ...mapState('operatorChat', ['answerTemplates', 'foundAnswerTemplates', 'answerTemplatesSearchTerms']),
    ...mapState('interest', ['directories']),
    ...mapGetters('activities', ['getActivityById']),
    ...mapGetters('user', ['isExternalOperator']),
    currentActivity() {
      return this.getActivityById(this.value.activityId);
    },
    currentDirectoriesWzMarkers() { // темы с маркерами, выбранные для текущей активности
      return this.currentActivity.markers.reduce(getDirectoriesFromMarkers, []);
    },
    sortedDirectories() { // темы, отсортированные по алфавиту (шаблон не выделен)
      let isFoundTemplateWithoutMarkers;
      if (!this.isSearchMode) { // не в режиме поиска формируем темы на основе подгруженных тем
        const orderedDirectories = orderBy(this.directories, [(i) => i.name.toLowerCase()]);
        isFoundTemplateWithoutMarkers = this.answerTemplates
          .some((template) => isEmpty(template.markers));
        if (isFoundTemplateWithoutMarkers) {
          // если есть шаблоны без маркеров, добавляем тему "без темы"
          return [this.serviceDirectory, ...orderedDirectories];
        }
        return orderedDirectories;
      }
      // в режиме поиска темы формируем на основе найденных шаблонов
      const foundTemplatesMarkers = this.foundAnswerTemplates.reduce(getMarkersFromTemplates, []);
      const foundTemplatesDirectories = foundTemplatesMarkers.reduce(getDirectoriesFromMarkers, []);
      const orderedfoundTemplatesDirectories = orderBy(foundTemplatesDirectories, [(i) => i.name.toLowerCase()]);
      isFoundTemplateWithoutMarkers = this.foundAnswerTemplates
        .some((template) => isEmpty(template.markers));
      if (isFoundTemplateWithoutMarkers) {
        // если есть шаблоны без маркеров, добавляем тему "без темы"
        return [this.serviceDirectory, ...orderedfoundTemplatesDirectories];
      }
      return orderedfoundTemplatesDirectories;
    },
    selectedTemplateDirectories() { // темы, отсортированные по алфавиту, для выделенного шаблона
      if (isEmpty(this.selectedTemplate.markers)) {
        return [this.serviceDirectory];
      }
      const selectedTemplateDirectories = this.selectedTemplate.markers.reduce(getDirectoriesFromMarkers, []);
      return orderBy(selectedTemplateDirectories, [(i) => i.name.toLowerCase()]);
    },
    selectedTemplateMarkersByDirectory() { // маркеры, отсортированные по алфавиту
      // для выделенного шаблона (выбрана тема или нет)
      return !this.selectedTemplateActiveDirectory ? [] : this.selectedTemplate.markers
        .filter((marker) => marker.directory.id === this.selectedTemplateActiveDirectory.id);
    },
    templatesWithoutMarkers() { // шаблоны без маркеров (не в режиме поиска)
      return this.answerTemplates.filter((template) => isEmpty(template.markers));
    },
    templatesInView() {
      // в режиме поиска
      if (this.isSearchMode) {
        return this.templatesInViewInSearchMode;
      }
      return this.templatesInViewInRegularMode;
    },
    templatesInViewInRegularMode() {
      if (this.activeDirectory?.id === 'serviceDirectory') return this.templatesWithoutMarkers;
      let setMarkers = []; // массив для всех нажатых маркеров
      let resTemplates = [];
      // setMarkersBefore - массив для ранее назначенных маркеров для активности
      const setMarkersBefore = this.markers
        .filter((marker) => this.currentActivity.markers.some((chosenMarker) => chosenMarker.id === marker.id));
      setMarkers = [...setMarkers, ...setMarkersBefore];
      const currentMarker = this.markers.find((marker) => marker.id === this.activeMarker?.id);
      // если юзер нажал дополнительный маркер,
      // добавляем этот маркер в список нажатых маркеров
      if (currentMarker) {
        setMarkers.push(currentMarker);
      }
      if (!isEmpty(setMarkers)) { // если есть хоть один нажатый маркер
        setMarkers.forEach((marker) => { // находим все шаблоны,
          // соответствующие нажатым маркерам среди текущих маркеров
          resTemplates = [...resTemplates, ...marker.answerTemplates];
        });
        if (!isEmpty(resTemplates)) {
          resTemplates = uniqBy(resTemplates, 'id');
          resTemplates = orderBy(resTemplates, [(i) => i.text.toLowerCase()]);
        }
      } else {
        // если нет нажатых маркеров
        // отображаем все доступные шаблоны для текущих маркеров текущего справочника
        const markersWithTemplates = filter(this.markers, (i) => i.answerTemplates.length > 0);
        if (markersWithTemplates.length) {
          markersWithTemplates.forEach((m) => {
            resTemplates = uniqBy(resTemplates.concat(m.answerTemplates), 'id');
            resTemplates = orderBy(resTemplates, [(i) => i.text.toLowerCase()]);
          });
        }
      }
      return resTemplates;
    },
    templatesInViewInSearchMode() {
      if (!this.activeDirectory) {
        return this.foundAnswerTemplates;
      }
      if (this.activeDirectory?.id === 'serviceDirectory') {
        return this.foundAnswerTemplates.filter((template) => isEmpty(template.markers));
      }
      if (this.activeMarker) {
        return this.foundAnswerTemplates
          .filter((template) => template.markers.some((marker) => marker.id === this.activeMarker.id));
      } return this.foundAnswerTemplates
        .filter((template) => template.markers.some((marker) => this.activeDirectory.markers
          .some((activeDirectoryMarker) => activeDirectoryMarker.id === marker.id)));
    },
    handleChosenMarkers() {
      const orderedByDirectories = orderBy(this.currentDirectoriesWzMarkers, [((item) => item.name.toLowerCase())]);
      const arr = orderedByDirectories
        .map((item) => {
          const { name, markers } = item;
          const markersNames = markers.map((marker) => marker.name);
          const markersNamesSorted = orderBy(markersNames, [(markerName) => markerName.toLowerCase()]);
          const markersStr = markersNamesSorted.join(', ');
          return `<div><span style="font-weight: bold">${name}:</span> ${markersStr}</div>`;
        });
      return arr.join('');
    },
    searchDisabled() {
      return (isEmpty(this.sortedDirectories) && !this.isSearchMode)
        || this.value.isClosed;
    },
    handleNoTemplatesText() {
      return this.isSearchMode
        ? `<span>${this.$t('T_NO_AVAILABLE_TEMPLATES_FOR_REQUEST')}: <span style="font-weight: bold">${this.sentForSearchText}</span></span>`
        : this.$t('T_NO_AVAILABLE_TEMPLATES');
    },
  },
  methods: {
    ...mapMutations('operatorChat', ['CLEAR_ANSWER_TEMPLATES_LOADED']),
    ...mapActions('operatorChat', ['updateMarkers', 'loadAnswerTemplates', 'searchAnswerTemplates']),
    ...mapActions('interest', ['fetchDirectories']),
    init() {
      this.fetchDirectories().then(() => {
        if (this.sortedDirectories.length) {
          this.onshowMarks(this.sortedDirectories[0]);
          return;
        } this.loadingFormData = false;
      });
    },
    emptyData() {
      this.selectedTemplate = null;
      this.activeDirectory = null;
      this.activeMarker = null;
      this.selectedTemplateActiveDirectory = null;
      this.selectedTemplateActiveMarker = null;
      this.isMarkerEdit = false;
      this.markers = [];
      this.selectedTemplateMarkers = [];
    },
    disabledMarker(id) {
      const isExist = this.currentActivity.markers.some((marker) => marker.id === id);
      if (this.selectedTemplate) {
        return this.selectedTemplateActiveMarker?.id === id || isExist;
      }
      return this.activeMarker?.id === id || isExist;
    },
    addMarkers(activeMarker, activeDirectory) {
      if (activeMarker) {
        this.loadingTemplates = true;
        setMarkerForActivity(this.value.activityId, activeMarker.id).then((response) => {
          this.isMarkerEdit = false;
          this.updateMarkers({
            activityId: this.value.activityId,
            ...response,
          });
          toast.info({
            msg: this.$t('T_ADDED_MARKER_FOR_THEME', [activeMarker.name.toUpperCase(), activeDirectory.name]),
            duration: 5000,
          });
          if (response.markers.length >= 1) this.value.autoCloseActivity(this.value.activityId);
        })
          .catch(() => {
            this.setMarkerError = true;
          })
          .finally(() => {
            this.loadingTemplates = false;
          });
      }
    },
    onshowMarks(directory) {
      this.loadingMarks = true;
      const lastDirectory = this.activeDirectory;
      this.activeDirectory = directory;
      if (directory.id !== 'serviceDirectory') {
        if (!this.isSearchMode) {
          markersInDirectory(directory.id).then((markers) => {
            this.markers = orderBy(markers, [(m) => m.name.toLowerCase()]);
          }).catch(() => {
            this.activeDirectory = lastDirectory;
          })
            .finally(() => {
              this.emptyMarkersData();
            });
        } else {
          this.markers = this.activeDirectory.markers;
          this.emptyMarkersData();
        }
      } else {
        this.markers = [];
        this.emptyMarkersData();
      }
    },
    emptyMarkersData() {
      this.loadingMarks = false;
      this.activeMarker = null;
      this.isMarkerEdit = false;
      this.loadingFormData = false;
    },
    onClickDirectoryBtn(event, directory) {
      event.stopPropagation();
      this.onshowMarks(directory);
    },
    onClickDirectoryBtnWrap(directory) {
      if (this.isSearchMode && !this.selectedTemplate && directory.id === this.activeDirectory?.id) {
        this.activeDirectory = null;
        this.activeMarker = null;
        this.markers = [];
      }
    },
    onClickSelectedTemplateDirectoryBtn(directory) {
      this.selectedTemplateActiveDirectory = directory;
      this.selectedTemplateActiveMarker = null;
      this.isMarkerEdit = false;
    },
    setActiveMarker(marker) {
      this.isMarkerEdit = true;
      this.setMarkerError = false;
      if (this.selectedTemplate) {
        this.selectedTemplateActiveMarker = marker;
      } else {
        this.activeMarker = marker;
      }
    },
    setTemplate(event, template) {
      event.stopPropagation();
      const parsedTemplate = parseAnswerTemplateForReplacement({
        template,
        customer: this.currentActivity.customer,
        isExternalOperator: this.isExternalOperator,
        interpolationCodes: this.$store.state.app[C_ANSWER_TEMPLATE_INTERPOLATION_CODES],
      });
      this.value.geneterateTemplate(parsedTemplate);
    },
    close() {
      this.$parent.close([null]);
    },
    save() {
      if (this.selectedTemplate) {
        this.addMarkers(this.selectedTemplateActiveMarker, this.selectedTemplateActiveDirectory);
      } else {
        this.addMarkers(this.activeMarker, this.activeDirectory);
      }
    },
    onSearchTemplates() {
      if (this.templateSearchText) {
        this.isSearchLoading = true;
        this.searchAnswerTemplates(this.templateSearchText)
          .then(() => {
            this.sentForSearchText = this.templateSearchText;
            this.isSearchLoading = false;
            this.isSearchMode = true;
            this.emptyData();
          })
          .catch(() => {
            toast.error({
              msg: this.$t('T_ANSWER_TEMPLATES_SEARCH_ERROR'),
            });
            this.sentForSearchText = '';
            this.isSearchLoading = false;
          });
      }
    },
    handleDirectoryColor(directory) {
      if (directory.id === 'serviceDirectory') {
        return 'orange';
      } return 'socialVk';
    },
    clickTemplate(template) {
      if (this.isSearchMode) {
        if (this.selectedTemplate?.id !== template.id) {
          this.selectedTemplate = template;
          if (this.selectedTemplateDirectories.length) {
            [this.selectedTemplateActiveDirectory] = this.selectedTemplateDirectories;
          }
        } else {
          this.selectedTemplate = null;
        }
      }
    },
    isSelectedTemplate(template) {
      return template.id === this.selectedTemplate?.id;
    },
    handleTemplateVal(templateStr) {
      if (!this.isSearchMode) {
        return templateStr;
      } return this.getHighlightedText(templateStr);
    },
    getHighlightedText(text) {
      const groups = this.answerTemplatesSearchTerms.map((v) => `(${v})`).join('|');
      const replaced = text.replace(new RegExp(groups, 'gi'), (g) => `<span style="background:#ffef04c9">${g}</span>`);
      return `<span>${replaced}<span>`;
    },
    mouseOver() {
      const ref = this.$refs.activeDirectory;
      this.showActiveDirectoryTooltip = ref?.scrollWidth > ref?.clientWidth;
    },
  },
};
</script>

<template lang="pug">
div
  div(:class="$style.form", v-if="!loadingFormData")
    header(:class="$style.formHeader")
      div(:class="$style.title") {{ $t('T_ADD_MARKER') }}
      div(:class="$style.icon", @click="close",
          tabindex="0", @keyup.enter="close")
        ui-icon(:class="$style.close", key="close", :title="$t('T_CLOSE')", data-test="CLOSE_BUTTON") close
    div(:class="$style.line")
    div(:class="$style.formBody")
      div(:class="$style.subTitle") {{ $t('T_MARKERS_SELECTED') }}:
      div(:class="$style.markersChosen" v-if="!!handleChosenMarkers")
        div(v-html="handleChosenMarkers")
      div(:class="$style.line")
      div(:class="[$style.table, isSearchMode && $style.tableSearch]")
        div(v-if="isSearchMode" :class="$style.searchModeIndicatorWrap")
          div(:class="$style.searchModeIndicator") {{ $t('T_SEARCH_MODE') }}
        div(:class="$style.formBodyHeaders")
          div(:class="$style.columnHeader")
              div(:class="$style.subTitle") {{ `${$t('T_ANSWER_TEMPLATE_THEMES')}:` }}
          div(:class="$style.columnHeader")
            div(:class="$style.markersColumnHeader") {{ `${$t('T_MARKERS_FOR_THEMES')}:` }}
            v-tooltip(right content-class="vuetifyTooltip" max-width="300px" v-if="activeDirectory && showActiveDirectoryTooltip" z-index="10000")
              template(v-slot:activator="{ on, attrs }")
                span(v-on="on" :class="$style.markerChosen" @mouseover="mouseOver" ref="activeDirectory") {{ activeDirectory.name }}
              span {{ activeDirectory.name }}
            span(:class="$style.markerChosen", v-if="activeDirectory && !showActiveDirectoryTooltip" @mouseover="mouseOver" ref="activeDirectory") {{ activeDirectory.name }}
          div(:class="$style.answerTemplatesHeaderWrap")
            div(:class="$style.subTitle") {{ `${$t('T_ANSWER_TEMPLATES')}:` }}
            div(:class="$style.searchWrap")
              v-text-field(:disabled="searchDisabled" v-model.trim="templateSearchText", hide-details, prepend-inner-icon="search", @keydown.enter.prevent="onSearchTemplates()" clearable)
        div(:class="$style.formBodyContentWrap")
          ui-progress-circle(v-if="isSearchLoading")
          div(v-if="!isSearchLoading" :class="$style.formBodyContent")
            div(:class="$style.columnContent")
              div(:class="$style.actions" v-if="!selectedTemplate")
                div(:class="$style.noDataText"
                    v-if="sortedDirectories.length === 0") {{ $t('T_NO_THEMES') }}
                div(v-for="(directory, index) in sortedDirectories" :class="$style.flexWrap" @click="onClickDirectoryBtnWrap(directory)" @keyup.enter="onClickDirectoryBtnWrap(directory)")
                  ui-button(data-test="THEME",
                    :class="[$style.actionsShape, $style.directory]",
                    :colored="handleDirectoryColor(directory)",
                    :disabled="activeDirectory && directory.id==activeDirectory.id",
                    size="large",
                    :key="directory.id",
                    @click.native="onClickDirectoryBtn($event, directory)",
                    @keyup.enter.native="onClickDirectoryBtn($event, directory)")
                    span(:class="$style.text") {{directory.name}}
              div(:class="$style.actions" v-if="selectedTemplate")
                div(v-for="(directory, index) in selectedTemplateDirectories" :class="$style.flexWrap")
                  ui-button(data-test="THEME",
                    :class="[$style.actionsShape, $style.directory]",
                    :colored="handleDirectoryColor(directory)",
                    :disabled="directory.id==selectedTemplateActiveDirectory.id",
                    size="large",
                    :key="directory.id",
                    @click.native="onClickSelectedTemplateDirectoryBtn(directory)",
                    @keyup.enter.native="onClickSelectedTemplateDirectoryBtn(directory)")
                    span(:class="$style.text") {{directory.name}}
            div(:class="$style.columnContent")
              div(v-if="!selectedTemplate")
                div(:class="$style.noDataText"
                v-if="markers.length === 0 && !loadingMarks") {{ $t('T_NO_MARKERS') }}
                div(:class="$style.notification" v-if="setMarkerError") {{ $t('T_ADD_MARKER_ERROR') }}
                div(:class="$style.actions" v-if="!loadingMarks")
                  div(:class="$style.flexWrap")
                    ui-button(data-test="MARKER",
                      v-for="(marker, index) in markers",
                      :class="[$style.actionsShape, $style.mark]",
                      colored="primary",
                      size="large",
                      :key="marker.id",
                      :disabled="disabledMarker(marker.id)",
                      @submit="setActiveMarker(marker)",
                      @keyup.enter.native="setActiveMarker(marker)",
                      v-if="!loadingMarks")
                      span(:class="$style.text") {{marker.name}}
                ui-progress-circle(v-if="loadingMarks")
              div(v-if="selectedTemplate")
                div(:class="$style.noDataText"
                v-if="selectedTemplateMarkersByDirectory.length === 0") {{ $t('T_NO_MARKERS') }}
                div(:class="$style.notification" v-if="setMarkerError") {{ $t('T_ADD_MARKER_ERROR') }}
                div(:class="$style.actions")
                  div(:class="$style.flexWrap")
                    ui-button(data-test="MARKER",
                      v-for="(marker, index) in selectedTemplateMarkersByDirectory",
                      :class="[$style.actionsShape, $style.mark]",
                      colored="primary",
                      size="large",
                      :key="marker.id",
                      :disabled="disabledMarker(marker.id)",
                      @submit="setActiveMarker(marker)",
                      @keyup.enter.native="setActiveMarker(marker)",
                      v-if="!loadingMarks")
                      span(:class="$style.text") {{marker.name}}
            div(:class="$style.wrapTemplates")
              div(:class="$style.templates", v-if="value.isClosed")
                div(:class="$style.noDataText") {{ $t('T_ACTIVITY_IS_CLOSED_IMPOSSIBLE_TO_ADD_ANSWER_TEMPLATE') }}
              div(:class="$style.templates", v-if="templatesInView.length === 0 && !loadingMarks && !value.isClosed")
                div(:class="$style.noDataText" v-html="handleNoTemplatesText")
              div(:class="$style.templates" v-if="!loadingMarks && !value.isClosed")
                div(:class="[$style.template, isSelectedTemplate(template) && $style.templateSelected]", :key="template.id", v-for="(template, index) in templatesInView" @click="clickTemplate(template)")
                  div(:class="$style.description", data-test="TEXT_TEMPLATE_LIST" disabled v-html="handleTemplateVal(template.value)")
                  div(:class="$style.action" data-test="REPLY_BUTTON")
                    ui-button(:class="[$style.actionsShape, $style.send]",
                      colored="primary",
                      size="large",
                      @click.native="setTemplate($event, template.value)",
                      @keyup.enter.native="setTemplate($event, template.value)"
                      data-test="SEND_MARKER_TEMPLATE")
                      span(:class="$style.text" v-if="template.value") {{ $t('T_ANSWER') }}
      div(:class="$style.saveBtnWrap" v-if="sortedDirectories.length !== 0 || isSearchMode")
        ui-button(colored="socialVk", @click.native="save",
        @keyup.enter.native="save", size="large", data-test="ADD_MARKER"
        :disabled="!isMarkerEdit || loadingTemplates || (activeDirectory && activeDirectory.id === 'serviceDirectory')") {{ $t('T_ADD_MARKER') }}
  ui-progress(:class="$style.loading", v-if="loadingFormData || loadingTemplates")
</template>

<style lang="sass">
.menu__content
  z-index: 10009
</style>

<style lang="sass" module>
.templates
  display: flex
  flex-direction: column
.template
  display: flex
  align-items: center
  margin-bottom: 20px
  background: #FFFFFF
  padding: 20px 0 20px 30px
  border-radius: 10px
  &Selected
    background: $color_bg_light_grey
  .description
    padding: 0
    color: $color_text
    font-weight: 400
    font-size: 16px
    text-overflow: ellipsis
    word-break: break-word
    white-space: pre-wrap
    flex: 4
    &.disabled
      cursor: default
      opacity: 0.7
.template:last-of-type
  margin-bottom: 0
.action
  display: flex
  margin: 0 8px 0 24px
  justify-content: space-around
.form
  display: block
  margin: 25px 48px 40px
  width: 1200px
.formHeader
  display: flex
  flex-grow: 1
  justify-content: center
  align-items: center
  font-size: 18px
  font-weight: 400
  padding-bottom: 2px
  margin-bottom: 10px
  margin-left: 12px
.formBody
  display: flex
  flex-direction: column

.formBodyHeaders
  display: flex
  margin-top: 10px
  height: 50px

.formBodyContent
  display: flex
  margin: 10px 0
  &Wrap
    height: 412px
    position: relative
.title
  display: flex
  justify-content: center
  align-items: center
  font-size: 18px
  font-weight: 400
  padding-bottom: 2px
  margin-left: 12px
  flex-grow: 1
.subTitle
  display: flex
  justify-content: flex-start
  font-size: 18px
  font-weight: 400
  text-overflow: ellipsis
  overflow: hidden
  white-space: nowrap
.center
  justify-content: center

.markers
  margin: 0 8px
  display: flex
  justify-content: center
  flex-wrap: wrap
.line
  border-bottom: 1px solid $color_divider
  margin: 8px 0
.content
  display: flex
  flex-direction: column
  min-width: 484px
  margin-bottom: 16px
  opacity: 1
  transition: height $a_hide_slow, opacity $a_speed
  .text
    color: $white
.actions
  overflow: auto
  height: 412px
  width: 200px
  &Shape
    padding: 0 8px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    display: flex
    justify-content: center
    align-items: center
    cursor: pointer
    margin-right: 8px
    margin-bottom: 8px
    &.directory
      border-radius: 16px
      margin-right: 0
    &.mark
      border-radius: 2px
      margin-right: 0
.send
  border-radius: 2px
  display: flex
  flex-grow: 1
  flex-shrink: 0
  margin: 0 20px
.icon
  width: 36px
  height: 36px
  display: flex
  align-items: center
  justify-content: center
  border-radius: 50%
  cursor: pointer
  &:hover
    background: $color_main_light_third
    color: $color_msg_dark
  &:focus
    background: $color_main_light_third
    color: $color_msg_dark
    outline: none
  .close
    width: 70%
.notification
  display: flex
  justify-content: center
  color: $color_red
  font-size: 12px
  &Hidden
    opacity: 0
.loading
  position: absolute
  left: 0
  right: 0
  bottom: 0
.noMarkers
  display: flex
  flex-direction: column
  align-items: center

.columnHeader
  width: 200px
  margin-right: 40px

.columnContent
  width: 200px
  margin-right: 40px
  height: 412px

.markersColumnHeader
  font-weight: 400
  font-size: 18px

.markerChosen
  display: inline-block
  text-overflow: ellipsis
  overflow: hidden
  white-space: nowrap
  max-width: 190px
  color: $color_text
.flexWrap
  display: flex
  flex-direction: column
.wrapTemplates
  overflow: auto
  height: 412px
  width: 58%
.markersChosen
  max-height: 64px
  overflow: auto
  margin: 5px 0
  overflow-wrap: break-word
  word-wrap: break-word
  word-break: break-all
  word-break: break-word
.saveBtnWrap
  display: flex
  justify-content: center
  margin-top: 20px

.answerTemplatesHeaderWrap
  width: 58%
  display: flex
  justify-content: space-between
.searchWrap
  width: 220px
  margin-top: -18px
.noDataText
  font-size: 16px
  margin-top: 5px

.table
  padding: 10px 20px 10px
  &Search
    margin-top: 14px
    border-radius: 16px
    border: 1px solid $color_social_vk
    -webkit-box-shadow: 0px 0px 8px 0px rgba(34, 60, 80, 0.2)
    -moz-box-shadow: 0px 0px 8px 0px rgba(34, 60, 80, 0.2)
    box-shadow: 0px 0px 8px 0px rgba(34, 60, 80, 0.2)

.searchModeIndicator
  padding: 0 10px
  background: $color_social_vk
  color: white
  border-radius: 16px
  &Wrap
    display: flex
    justify-content: flex-end
</style>
